import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Gauge, Fuel, Settings2, CalendarDays, MessageSquare, Expand } from "lucide-react";
import { api, resolveImage } from "@/lib/api";
import { useI18n } from "@/i18n/LanguageContext";
import ImageLightbox from "@/components/ImageLightbox";

function formatPrice(n, lang) {
  const locale = lang === "gr" ? "el-GR" : "en-US";
  try {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency: "EUR",
      maximumFractionDigits: 0,
    }).format(n);
  } catch {
    return `€${n}`;
  }
}

function statusClass(status) {
  if (status === "reserved") return "status-reserved";
  if (status === "sold") return "status-sold";
  return "status-available";
}

export default function VehicleDetail() {
  const { t, lang } = useI18n();
  const { id } = useParams();
  const navigate = useNavigate();
  const [v, setV] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lightboxIndex, setLightboxIndex] = useState(null);

  useEffect(() => {
    setLoading(true);
    api
      .get(`/vehicles/${id}`)
      .then((r) => setV(r.data))
      .catch(() => setV(null))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-6 md:px-10 pt-[110px] md:pt-[128px] pb-20 text-zinc-500 text-sm tracking-widest uppercase">
        {t("vehicle.loading")}
      </div>
    );
  }

  if (!v) {
    return (
      <div
        className="max-w-7xl mx-auto px-6 md:px-10 pt-[110px] md:pt-[128px] pb-20"
        data-testid="vehicle-not-found"
      >
        <h1 className="font-display text-3xl">{t("vehicle.notFound")}</h1>
        <button onClick={() => navigate("/garage")} className="btn-ghost mt-6">
          <ArrowLeft size={14} /> {t("vehicle.back")}
        </button>
      </div>
    );
  }

  const images = v.images?.length ? v.images : [];
  const mileage = (v.mileage || 0).toLocaleString(lang === "gr" ? "el-GR" : "en-US");

  return (
    <div
      className="max-w-7xl mx-auto px-6 md:px-10 pt-[110px] md:pt-[128px] pb-24"
      data-testid="vehicle-detail-page"
    >
      <Link
        to="/garage"
        className="inline-flex items-center gap-2 text-xs tracking-[0.3em] uppercase text-zinc-400 hover:text-[#C6A15B] mb-8"
        data-testid="back-to-garage"
      >
        <ArrowLeft size={14} /> {t("vehicle.back")}
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-8">
          {/* HERO IMAGE — click to open lightbox at index 0 */}
          <button
            type="button"
            onClick={() => images.length && setLightboxIndex(0)}
            disabled={!images.length}
            className="group relative aspect-[16/10] w-full bg-[#121212] border border-white/5 overflow-hidden cursor-zoom-in disabled:cursor-default"
            data-testid="vehicle-hero-image"
            aria-label="Open image gallery"
          >
            {images[0] ? (
              <img
                src={resolveImage(images[0])}
                alt={`${v.brand} ${v.model}`}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.02]"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-zinc-600 text-xs uppercase tracking-widest">
                No Image
              </div>
            )}
            <span className={`status-chip ${statusClass(v.status)} absolute top-5 left-5`}>
              {t(`vehicle.status.${v.status}`)}
            </span>
            {images.length > 0 && (
              <span className="absolute bottom-4 right-4 inline-flex items-center gap-2 bg-black/55 backdrop-blur-sm text-[10px] tracking-[0.32em] uppercase text-white px-3 py-2 border border-white/15 group-hover:border-[#C6A15B] group-hover:text-[#C6A15B] transition-colors">
                <Expand size={12} />
                {String(images.length).padStart(2, "0")}
              </span>
            )}
          </button>

          {/* REMAINING IMAGES — full grid, every photo, click to open at that index */}
          {images.length > 1 && (
            <div
              className="mt-3 grid grid-cols-3 md:grid-cols-4 gap-3"
              data-testid="vehicle-thumbnails"
            >
              {images.slice(1).map((im, i) => {
                const realIndex = i + 1;
                return (
                  <button
                    key={realIndex}
                    type="button"
                    onClick={() => setLightboxIndex(realIndex)}
                    className="group aspect-[4/3] overflow-hidden border border-white/10 hover:border-[#C6A15B] transition-colors cursor-zoom-in"
                    data-testid={`vehicle-thumb-${realIndex}`}
                    aria-label={`Open image ${realIndex + 1}`}
                  >
                    <img
                      src={resolveImage(im)}
                      alt=""
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.05]"
                    />
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="lg:col-span-4">
          <div className="text-[11px] tracking-[0.32em] uppercase text-[#C6A15B] mb-2">{v.brand}</div>
          <h1 className="font-display text-3xl md:text-4xl font-light leading-tight">
            {v.model}
          </h1>
          <div className="mt-6 flex items-baseline gap-3">
            <div className="font-display text-3xl text-white">{formatPrice(v.price, lang)}</div>
          </div>

          <div className="mt-8 grid grid-cols-2 gap-[1px] bg-white/5">
            <Spec icon={<CalendarDays size={14} />} label={t("vehicle.spec.year")} value={v.year} />
            <Spec
              icon={<Gauge size={14} />}
              label={t("vehicle.spec.mileage")}
              value={`${mileage} km`}
            />
            <Spec icon={<Fuel size={14} />} label={t("vehicle.spec.fuel")} value={v.fuel} />
            <Spec
              icon={<Settings2 size={14} />}
              label={t("vehicle.spec.transmission")}
              value={v.transmission}
            />
          </div>

          {v.description && (
            <p className="mt-8 text-zinc-300 text-sm leading-relaxed">{v.description}</p>
          )}

          <div className="mt-8 flex flex-col gap-3">
            <Link
              to={`/request?vehicle=${encodeURIComponent(v.id)}&preference=${encodeURIComponent(
                `${v.brand} ${v.model}`
              )}`}
              className="btn-gold w-full justify-center"
              data-testid="detail-enquire"
            >
              <MessageSquare size={14} /> {t("vehicle.enquire")}
            </Link>
            <Link
              to="/contact"
              className="btn-ghost w-full justify-center"
              data-testid="detail-contact"
            >
              {t("vehicle.bookAppointment")}
            </Link>
          </div>
        </div>
      </div>

      {lightboxIndex !== null && images.length > 0 && (
        <ImageLightbox
          images={images}
          startIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          alt={`${v.brand} ${v.model}`}
        />
      )}
    </div>
  );
}

function Spec({ icon, label, value }) {
  return (
    <div className="bg-[#0B0B0B] p-5">
      <div className="flex items-center gap-2 text-zinc-500 text-[10px] tracking-[0.3em] uppercase">
        {icon}
        {label}
      </div>
      <div className="text-white text-sm font-medium mt-2">{value}</div>
    </div>
  );
}
